import { Action, Select, Selector, State, StateContext } from "@ngxs/store";
import { Address } from "../model/address-model";
import { Injectable } from "@angular/core";
import { AddressService } from "../service/address.service";
import {
  ClearAddress,
  CreateAddress,
  DeleteAddress,
  GetAddresses,
  UpdateAddress} from "./address.action";
import {
  catchError,
  iif,
  Observable,
  of,
  switchMap,
  tap,
  throwError} from "rxjs";
import { addressMapper } from "../mapper/address-mapper";
import { NotificationService } from "src/app/shared/services/notification.service";
import { HttpErrorResponse } from "@angular/common/http";
import { AuthState } from "../../auth/state/auth.state";

export class AddressStateModel {
  addresses: Address[];
  loading: boolean;
}

@State<AddressStateModel>({
  name: "address",
  defaults: {
    addresses: [],
    loading: false}})
@Injectable()
export class AddressState {
  @Select(AuthState.profileId) profileId$: Observable<string | undefined>;

  constructor(
    private _addressService: AddressService,
    private _notificationService: NotificationService
  ) {}

  @Selector()
  static addresses(state: AddressStateModel) {
    return state.addresses;
  }

  @Selector()
  static loading(state: AddressStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static addressById(state: AddressStateModel) {
    return (addressId: string) => {
      return state.addresses.find((address) => address.id === addressId);
    };
  }

  @Action(GetAddresses)
  getAddresses(ctx: StateContext<AddressStateModel>, action: GetAddresses) {
    const state = ctx.getState();
    if (state.addresses.length > 0) {
      return;
    }
    ctx.patchState({ loading: true });
    return this.profileId$
      .pipe(
        switchMap((profileId) =>
          iif(
            () => !!profileId,
            this._addressService.getAddresses(profileId!),
            of([])
          )
        ),
        tap((addresses) => {
          ctx.patchState({
            addresses: addresses.map((address) => addressMapper(address)),
            loading: false});
        }),
        catchError((error: HttpErrorResponse) => {
          ctx.patchState({ loading: false });
          if (error.status === 404) {
            return throwError(() => error);
          }
          this._notificationService.showError(error.message);
          return throwError(() => error);
        })
      )
      .subscribe();
  }

  @Action(CreateAddress)
  createAddress(ctx: StateContext<AddressStateModel>, action: CreateAddress) {
    const state = ctx.getState();
    return this.profileId$
      .pipe(
        switchMap((profileId) =>
          iif(
            () => !!profileId,
            this._addressService.createAddress({
              ...action.payload,
              profileId}),
            of({} as any)
          )
        ),
        tap((address) => {
          ctx.patchState({
            addresses: [...state.addresses, addressMapper(address)]});
        }),
        catchError((error) => {
          this._notificationService.showError(error);
          return throwError(() => error);
        })
      )
      .subscribe();
  }

  @Action(UpdateAddress)
  updateAddress(ctx: StateContext<AddressStateModel>, action: UpdateAddress) {
    const state = ctx.getState();
    return this._addressService
      .updateAddress(action.addressId, action.payload)
      .pipe(
        tap((newAddress) => {
          const updatedAddresses = state.addresses.map((address) =>
            address.id === action.addressId
              ? addressMapper(newAddress)
              : address
          );
          ctx.patchState({
            addresses: updatedAddresses});
        }),
        catchError((error) => {
          this._notificationService.showError(error);
          return throwError(() => error);
        })
      );
  }

  @Action(DeleteAddress)
  deleteAddress(ctx: StateContext<AddressStateModel>, action: DeleteAddress) {
    const state = ctx.getState();
    return this._addressService.deleteAddress(action.addressId).pipe(
      tap(() => {
        ctx.patchState({
          addresses: state.addresses.filter(
            (address) => address.id !== action.addressId
          )});
      }),
      catchError((error) => {
        this._notificationService.showError(error);
        return throwError(() => error);
      })
    );
  }

  @Action(ClearAddress)
  clearAddress(ctx: StateContext<AddressStateModel>) {
    ctx.patchState({
      addresses: []});
    return of();
  }
}
